
// Helper functions to trigger onboarding events

/**
 * Trigger when user updates their profile characteristics
 * Should be called when calculating and saving profile data
 */
export const triggerProfileUpdate = () => {
  console.log("[EVENT] Dispatching profile-updated event");
  const event = new CustomEvent('profile-updated');
  window.dispatchEvent(event);
  console.log("[EVENT] profile-updated event dispatched successfully");
};

/**
 * Trigger when user adds a recipe to favorites
 * Should be called when clicking 'Guardar' in any recipe
 */
export const triggerFavoriteAdd = () => {
  console.log("[EVENT] Dispatching favorite-added event");
  const event = new CustomEvent('favorite-added', {
    detail: { timestamp: new Date().toISOString() }
  });
  
  window.dispatchEvent(event);
  document.dispatchEvent(event);
  
  console.log("[EVENT] favorite-added event dispatched successfully");
  
  // Update social challenge progress
  updateSocialChallenge('favorites');
};

/**
 * Trigger when user adds a recipe to weekly menu
 * Should be called when adding a recipe to the weekly menu
 */
export const triggerMenuAdd = () => {
  console.log("[EVENT] Dispatching menu-added event");
  const event = new CustomEvent('menu-added');
  window.dispatchEvent(event);
  console.log("[EVENT] menu-added event dispatched successfully");
};

/**
 * Trigger when user starts a challenge
 * Should be called when a user clicks the "Iniciar reto" button
 */
export const triggerChallengeStart = () => {
  console.log("[EVENT] Dispatching challenge-started event from utility function");
  
  // Create event with more detailed information
  const event = new CustomEvent('challenge-started', {
    detail: { 
      timestamp: new Date().toISOString()
    }
  });
  
  // Dispatch to both window and document for maximum compatibility
  window.dispatchEvent(event);
  document.dispatchEvent(event);
  
  console.log("[EVENT] challenge-started event dispatched successfully from utility function");
};

/**
 * Trigger when user interacts with the nutritionist
 * Should be called when clicking 'Hablar con un nutricionista'
 */
export const triggerNutritionistChat = () => {
  console.log("[EVENT] Dispatching nutritionist-chat event");
  const event = new CustomEvent('nutritionist-chat');
  window.dispatchEvent(event);
  document.dispatchEvent(event);
  console.log("[EVENT] nutritionist-chat event dispatched successfully");
};

/**
 * Trigger when user interacts with Lisa (AI assistant)
 * Should be called when starting a chat with Lisa
 */
export const triggerLisaChat = () => {
  console.log("[EVENT] Dispatching lisa-chat event");
  // Dispatch a custom event that will be caught by the SimpleOnboardingChecklist component
  const event = new CustomEvent('lisa-chat', {
    detail: { timestamp: new Date().toISOString() }
  });
  window.dispatchEvent(event);
  document.dispatchEvent(event);
  console.log("[EVENT] lisa-chat event dispatched successfully");
};

/**
 * Trigger when user comments on a recipe
 * Should be called when submitting a comment
 */
export const triggerCommentAdd = () => {
  console.log("[EVENT] Dispatching comment-added event");
  const event = new CustomEvent('comment-added', {
    detail: { timestamp: new Date().toISOString() }
  });
  
  window.dispatchEvent(event);
  document.dispatchEvent(event);
  console.log("[EVENT] comment-added event dispatched successfully");
  
  // Update social challenge progress
  updateSocialChallenge('comments');
};

/**
 * Trigger when user likes a recipe
 * Should be called from the like button component
 */
export const triggerLikeAdd = () => {
  console.log("[EVENT] Dispatching like-added event");
  const event = new CustomEvent('like-added', {
    detail: { timestamp: new Date().toISOString() }
  });
  
  window.dispatchEvent(event);
  console.log("[EVENT] like-added event dispatched successfully");
  
  // Update social challenge progress
  updateSocialChallenge('likes');
};

/**
 * Trigger when user points are updated
 * Should be called when a challenge is completed or points are awarded
 * @param userId - The id of the user
 * @param newPoints - The new total points value or amount to add
 * @param isAdditive - If true, newPoints is added to existing points; if false, newPoints replaces existing points
 */
export const triggerPointsUpdated = (userId: string, newPoints: number, isAdditive: boolean = true) => {
  console.log(`[EVENT] Dispatching points-updated event for user ${userId} with ${newPoints} points (${isAdditive ? 'additive' : 'absolute'})`);
  
  try {
    // If additive, get current points first
    if (isAdditive) {
      // Get current points from localStorage
      const currentPoints = Number(localStorage.getItem(`user_points_${userId}`) || '0');
      const totalPoints = currentPoints + newPoints;
      
      // Store the updated total
      localStorage.setItem(`user_points_${userId}`, totalPoints.toString());
      
      // Create and dispatch the event with the total
      const event = new CustomEvent('points-updated', {
        detail: { userId, newPoints: totalPoints }
      });
      
      window.dispatchEvent(event);
      document.dispatchEvent(event);
      
      console.log(`[EVENT] points-updated event dispatched with total ${totalPoints} points`);
    } else {
      // Absolute points update (not additive)
      localStorage.setItem(`user_points_${userId}`, newPoints.toString());
      
      const event = new CustomEvent('points-updated', {
        detail: { userId, newPoints }
      });
      
      window.dispatchEvent(event);
      document.dispatchEvent(event);
      
      console.log(`[EVENT] points-updated event dispatched with absolute ${newPoints} points`);
    }
  } catch (error) {
    console.error('[EVENT] Error in triggerPointsUpdated:', error);
  }
};

/**
 * Trigger challenge completed event with points
 * @param userId - The id of the user
 * @param challengeId - The id of the completed challenge
 * @param pointsEarned - Points earned for completing the challenge
 */
export const triggerChallengeCompleted = (userId: string, challengeId: number, pointsEarned: number = 100) => {
  console.log(`[EVENT] Dispatching challenge-completed event for challenge ${challengeId} with ${pointsEarned} points`);
  
  try {
    // First dispatch the challenge-completed event
    const challengeEvent = new CustomEvent('challenge-completed', {
      detail: { 
        challengeId,
        userId,
        points: pointsEarned
      }
    });
    
    window.dispatchEvent(challengeEvent);
    document.dispatchEvent(challengeEvent);
    
    console.log(`[EVENT] challenge-completed event dispatched successfully`);
  } catch (error) {
    console.error('[EVENT] Error in triggerChallengeCompleted:', error);
  }
};

/**
 * Trigger personalized feedback from Lisa
 * @param userId - The id of the user
 * @param feedbackType - Type of feedback to provide (water, nutrition, activity)
 * @param data - Additional data related to the feedback
 */
export const triggerLisaFeedback = (userId: string, feedbackType: 'water' | 'nutrition' | 'activity' | 'success', data?: any) => {
  console.log(`[EVENT] Dispatching lisa-feedback event for user ${userId}, type: ${feedbackType}`);
  const event = new CustomEvent('lisa-feedback', {
    detail: { 
      userId, 
      feedbackType, 
      data,
      timestamp: new Date().toISOString() 
    }
  });
  
  window.dispatchEvent(event);
  console.log("[EVENT] lisa-feedback event dispatched successfully");
};

/**
 * Update social challenge progress
 * @param actionType - The type of social action (likes, comments, favorites)
 */
export const updateSocialChallenge = (actionType: 'likes' | 'comments' | 'favorites') => {
  if (!window || !localStorage) return;
  
  const userId = JSON.parse(localStorage.getItem('sb-fxjyyqdntzslxnqfcelf-auth-token') || '{}')?.user?.id;
  if (!userId) return;
  
  const challengeKey = `challenge_social_${userId}`;
  
  let challenge = JSON.parse(localStorage.getItem(challengeKey) || 'null');
  
  if (!challenge) {
    challenge = {
      id: 3, // Fixed to match the challenge ID in HomeChallenges
      title: "5 Social",
      description: "Da 5 likes a recetas, escribe 5 comentarios y guarda 5 recetas en favoritos.",
      duration: "Sin límite",
      difficulty: "Principiante",
      icon: "Users",
      type: "social",
      completed: false,
      startDate: new Date().toISOString(),
      endDate: null,
      progress: 0,
      objectives: {
        likes: 0,
        comments: 0,
        favorites: 0,
        requiredCount: 5
      },
      points: 100
    };
  }
  
  // Make sure we have valid objectives data
  if (!challenge.objectives) {
    challenge.objectives = {
      likes: 0,
      comments: 0,
      favorites: 0,
      requiredCount: 5
    };
  }
  
  // Increment the appropriate counter
  if (actionType in challenge.objectives && challenge.objectives[actionType] < challenge.objectives.requiredCount) {
    challenge.objectives[actionType]++;
    
    // Calculate progress (average of the three objectives)
    const { likes = 0, comments = 0, favorites = 0, requiredCount = 5 } = challenge.objectives;
    const progress = ((likes / requiredCount) + (comments / requiredCount) + (favorites / requiredCount)) / 3 * 100;
    challenge.progress = Math.min(Math.round(progress), 100);
    
    // Check if challenge is completed
    if (likes >= requiredCount && comments >= requiredCount && favorites >= requiredCount && !challenge.completed) {
      challenge.completed = true;
      challenge.endDate = new Date().toISOString(); // Add end date when challenge is completed
      
      // Store badge - using both 'social' and 'social5' keys for compatibility
      localStorage.setItem(`badge_social5_${userId}`, 'true');
      sessionStorage.setItem(`badge_social5_${userId}`, 'true');
      localStorage.setItem(`badge_social_${userId}`, 'true');
      sessionStorage.setItem(`badge_social_${userId}`, 'true');
      
      // Store the date
      const currentDate = new Date().toISOString();
      localStorage.setItem(`badge_social5_date_${userId}`, currentDate);
      localStorage.setItem(`badge_social_date_${userId}`, currentDate);
      
      // Dispatch challenge completed event with points
      const challengeCompletedEvent = new CustomEvent('challenge-completed', {
        detail: { 
          challengeId: 3,
          userId,
          points: 100
        }
      });
      
      window.dispatchEvent(challengeCompletedEvent);
      document.dispatchEvent(challengeCompletedEvent);
      
      console.log('[CHALLENGE] Social challenge completed! Dispatched challenge-completed event');
    }
    
    // Ensure the challenge object doesn't contain complex nested objects that can't be serialized properly
    const cleanChallenge = { ...challenge };
    
    // Save updated challenge
    localStorage.setItem(challengeKey, JSON.stringify(cleanChallenge));
    sessionStorage.setItem(challengeKey, JSON.stringify(cleanChallenge));
    
    console.log(`[CHALLENGE] Updated social challenge - ${actionType}: ${challenge.objectives[actionType]}`);
  }
};
